/**
 * WorkflowNode (P2.1-S5 polish).
 *
 * Trước: solid bg-color-50 + border-color-400 flat.
 * Sau: gradient (color-50 → color-100/50) + shadow tăng dần (sm → md hover → xl selected),
 * border tinh tế hơn, typography rõ ràng hơn.
 */
import {
  Bot,
  Box,
  Clock,
  Flag,
  GitBranch,
  Hand,
  Layers,
  Mail,
  Play,
  Square,
  Wrench,
} from "lucide-react";
import * as React from "react";
import { Handle, type NodeProps, Position } from "reactflow";

import type { NodeType } from "@/api/workflows";

interface NodeStyle {
  icon: React.ReactNode;
  /** Tailwind classes cho gradient background + border + text */
  classes: string;
  /** Accent color cho icon container */
  accent: string;
}

const STYLES: Record<NodeType, NodeStyle> = {
  trigger: {
    icon: <Play className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-400/60 text-emerald-900 dark:from-emerald-950/40 dark:to-emerald-900/20 dark:text-emerald-100",
    accent: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  llm: {
    icon: <Bot className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-violet-50 to-violet-100/50 border-violet-400/60 text-violet-900 dark:from-violet-950/40 dark:to-violet-900/20 dark:text-violet-100",
    accent: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  },
  tool: {
    icon: <Wrench className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-400/60 text-blue-900 dark:from-blue-950/40 dark:to-blue-900/20 dark:text-blue-100",
    accent: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  },
  form: {
    icon: <Flag className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-cyan-50 to-cyan-100/50 border-cyan-400/60 text-cyan-900 dark:from-cyan-950/40 dark:to-cyan-900/20 dark:text-cyan-100",
    accent: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
  },
  branch: {
    icon: <GitBranch className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-400/60 text-amber-900 dark:from-amber-950/40 dark:to-amber-900/20 dark:text-amber-100",
    accent: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
  parallel: {
    icon: <Layers className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-yellow-400/60 text-yellow-900 dark:from-yellow-950/40 dark:to-yellow-900/20 dark:text-yellow-100",
    accent: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
  },
  human: {
    icon: <Hand className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-400/60 text-orange-900 dark:from-orange-950/40 dark:to-orange-900/20 dark:text-orange-100",
    accent: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  },
  notify: {
    icon: <Mail className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-pink-50 to-pink-100/50 border-pink-400/60 text-pink-900 dark:from-pink-950/40 dark:to-pink-900/20 dark:text-pink-100",
    accent: "bg-pink-500/15 text-pink-700 dark:text-pink-300",
  },
  delay: {
    icon: <Clock className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-slate-50 to-slate-100/50 border-slate-400/60 text-slate-900 dark:from-slate-900/40 dark:to-slate-800/20 dark:text-slate-100",
    accent: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
  },
  subflow: {
    icon: <Box className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-400/60 text-purple-900 dark:from-purple-950/40 dark:to-purple-900/20 dark:text-purple-100",
    accent: "bg-purple-500/15 text-purple-700 dark:text-purple-300",
  },
  end: {
    icon: <Square className="size-3.5" />,
    classes:
      "bg-gradient-to-br from-red-50 to-red-100/50 border-red-400/60 text-red-900 dark:from-red-950/40 dark:to-red-900/20 dark:text-red-100",
    accent: "bg-red-500/15 text-red-700 dark:text-red-300",
  },
};

interface NodeData {
  label: string;
  nodeType: NodeType;
  config: Record<string, unknown>;
}

export function WorkflowNodeComponent({
  data,
  selected,
}: NodeProps<NodeData>): JSX.Element {
  const style = STYLES[data.nodeType] ?? STYLES.tool;
  const isBranch = data.nodeType === "branch";
  const isTrigger = data.nodeType === "trigger";
  const isEnd = data.nodeType === "end";

  return (
    <div
      className={[
        "rounded-lg border-2 transition-all min-w-[170px] backdrop-blur-[1px]",
        style.classes,
        selected
          ? "shadow-xl ring-2 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
          : "shadow-md hover:shadow-lg",
      ].join(" ")}
    >
      {!isTrigger ? (
        <Handle
          type="target"
          position={Position.Top}
          className="!bg-foreground !w-2.5 !h-2.5 !border-background !border-[1.5px]"
        />
      ) : null}

      <div className="px-3 py-2">
        <div className="flex items-center gap-2">
          <div
            className={`rounded-md p-1 ${style.accent} flex items-center justify-center`}
          >
            {style.icon}
          </div>
          <div className="text-[10px] uppercase font-bold opacity-70 tracking-wider">
            {data.nodeType}
          </div>
        </div>
        <div className="text-sm font-semibold mt-1.5 truncate">
          {data.label}
        </div>
        <NodeSummary nodeType={data.nodeType} config={data.config} />
      </div>

      {isBranch ? (
        <>
          <Handle
            id="true"
            type="source"
            position={Position.Bottom}
            style={{ left: "30%" }}
            className="!bg-emerald-600 !w-2.5 !h-2.5 !border-background !border-[1.5px]"
          />
          <Handle
            id="false"
            type="source"
            position={Position.Bottom}
            style={{ left: "70%" }}
            className="!bg-rose-600 !w-2.5 !h-2.5 !border-background !border-[1.5px]"
          />
        </>
      ) : !isEnd ? (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!bg-foreground !w-2.5 !h-2.5 !border-background !border-[1.5px]"
        />
      ) : null}
    </div>
  );
}

function NodeSummary({
  nodeType,
  config,
}: {
  nodeType: NodeType;
  config: Record<string, unknown>;
}): JSX.Element | null {
  if (nodeType === "llm" && typeof config.prompt === "string") {
    const p = config.prompt;
    return (
      <div className="text-[10px] mt-1 opacity-70 truncate">
        {p.slice(0, 60) || "(empty prompt)"}
      </div>
    );
  }
  if (nodeType === "tool" && typeof config.tool_name === "string") {
    return (
      <code className="text-[10px] mt-1 block opacity-80">
        {config.tool_name || "(no tool)"}
      </code>
    );
  }
  if (nodeType === "form" && typeof config.form_code === "string") {
    return (
      <code className="text-[10px] mt-1 block opacity-80">
        {config.form_code || "(no form)"}
      </code>
    );
  }
  if (nodeType === "branch" && config.condition) {
    return (
      <div className="text-[10px] mt-1 opacity-70 truncate">
        if condition...
      </div>
    );
  }
  if (nodeType === "human") {
    return (
      <div className="text-[10px] mt-1 opacity-70">
        {String(config.title ?? "Approval")}
      </div>
    );
  }
  if (nodeType === "notify") {
    const channels = Array.isArray(config.channels)
      ? (config.channels as string[]).join(", ")
      : "";
    return <div className="text-[10px] mt-1 opacity-70">{channels}</div>;
  }
  if (nodeType === "delay") {
    return (
      <div className="text-[10px] mt-1 opacity-70">
        {String(config.duration_seconds ?? 0)}s
      </div>
    );
  }
  return null;
}

export const NODE_TYPES = {
  workflowNode: WorkflowNodeComponent,
};
